import type { CodeSymbol } from '../symbols/types';

/**
 * Produces a code outline for one or more file extensions. Built-ins register an
 * in-process provider; third-party plugins register one backed by their worker.
 * The registry does not care which.
 */
export interface OutlineProvider {
  /** Stable id (built-in name or plugin id). */
  id: string;
  /** Lowercase extensions (no dot) this provider outlines, e.g. ['ts', 'tsx']. */
  extensions: readonly string[];
  /** Produce the outline for `source`. `fileName` lets a provider pick a dialect. */
  getSymbols(source: string, fileName: string): Promise<CodeSymbol[]>;
}

export interface OutlineRegistry {
  /** Register a provider; returns a disposer that removes it. */
  register(p: OutlineProvider): () => void;
  /** The provider for a file by extension, or null. Most-recent registration wins. */
  providerForFile(fileName: string): OutlineProvider | null;
  /** Subscribe to registry changes (register / dispose). */
  subscribe(cb: () => void): () => void;
}

function extOf(name: string): string {
  const dot = name.lastIndexOf('.');
  if (dot < 0 || dot === name.length - 1) return '';
  return name.slice(dot + 1).toLowerCase();
}

export function createOutlineRegistry(): OutlineRegistry {
  // Newest first, so a later registration overrides an earlier one.
  const providers: OutlineProvider[] = [];
  const listeners = new Set<() => void>();
  const emit = (): void => listeners.forEach((l) => l());

  return {
    register(p: OutlineProvider): () => void {
      providers.unshift(p);
      emit();
      return () => {
        const i = providers.indexOf(p);
        if (i >= 0) {
          providers.splice(i, 1);
          emit();
        }
      };
    },
    providerForFile(fileName: string): OutlineProvider | null {
      const ext = extOf(fileName);
      if (ext === '') return null;
      return providers.find((p) => p.extensions.includes(ext)) ?? null;
    },
    subscribe(cb: () => void): () => void {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
}

/** The shared registry instance the app resolves outlines through. */
export const outlineRegistry: OutlineRegistry = createOutlineRegistry();
